import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Lightbulb, ChevronRight, ChevronLeft, X, Feather } from 'lucide-react';

const AUTHOR_TIPS = [
  {
    author: "Ernest Hemingway",
    image: "/hemingway-bg.jpg",
    tips: [
      "Write drunk, edit sober. Get your raw ideas down first.",
      "Use short sentences. They hit harder.",
      "The first draft of anything is garbage. Keep writing anyway.",
      "Write one true sentence. The truest sentence you know.",
      "Stop writing when you know what happens next. It makes starting easier tomorrow."
    ]
  },
  {
    author: "Stephen King",
    image: "/mark-twain-bg.jpg", // placeholder
    tips: [
      "If you want to be a writer, read a lot and write a lot.",
      "The road to hell is paved with adverbs.",
      "Kill your darlings, even when it breaks your heart.",
      "Write with the door closed, rewrite with the door open.",
      "Amateurs sit and wait for inspiration. The rest of us get up and go to work."
    ]
  },
  {
    author: "Virginia Woolf",
    image: "/virginia-woolf-bg.jpg",
    tips: [
      "Lock up your library if you like; but there is no lock that can shut the free mind.",
      "Arrange whatever pieces come your way.",
      "A woman must have money and a room of her own to write.",
      "Let us not take for granted the act of writing.",
      "The eyes of others are our prisons; their thoughts our cages."
    ]
  },
  {
    author: "Mark Twain",
    image: "/mark-twain-bg.jpg",
    tips: [
      "The secret of getting ahead is getting started.",
      "Use the right word, not its second cousin.",
      "Don't say the old lady screamed. Bring her on and let her scream.",
      "Substitute 'damn' every time you're inclined to write 'very.'",
      "Write without pay until somebody offers to pay."
    ]
  },
  {
    author: "Oscar Wilde",
    image: "/oscar-wilde-bg.jpg",
    tips: [
      "Be yourself; everyone else is already taken.",
      "I am so clever that sometimes I don't understand a word of what I'm saying.",
      "A good friend will always stab you in the front.",
      "Always forgive your enemies; nothing annoys them so much.",
      "The only way to get rid of a temptation is to yield to it."
    ]
  },
  {
    author: "Edgar Allan Poe",
    image: "/edgar-poe-bg.jpg",
    tips: [
      "Words have no power to impress without the exquisite horror of their reality.",
      "All that we see or seem is but a dream within a dream.",
      "The true genius shudders at incompleteness.",
      "There is no exquisite beauty without some strangeness.",
      "I have great faith in fools; self-confidence my friends call it."
    ]
  },
  {
    author: "Jane Austen",
    image: "/jane-austen-bg.jpg",
    tips: [
      "One half of the world cannot understand the pleasures of the other.",
      "A lady's imagination is very rapid.",
      "There is nothing like staying home for real comfort.",
      "It is a truth universally acknowledged... start with what everyone knows.",
      "Run mad as often as you choose; but do not faint."
    ]
  },
  {
    author: "Charles Dickens",
    image: "/charles-dickens-bg.jpg",
    tips: [
      "There is nothing in the world so irresistibly contagious as laughter.",
      "Have a heart that never hardens, a temper that never tires, a touch that never hurts.",
      "No one is useless in this world who lightens the burdens of another.",
      "Create characters the reader will never forget.",
      "The pain of parting is nothing to the joy of meeting again."
    ]
  }
];

export function AuthorTipsSidebar({ isOpen, onClose, onToggle }) {
  const [currentAuthorIndex, setCurrentAuthorIndex] = useState(0);
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [imageError, setImageError] = useState(false);

  const currentAuthor = AUTHOR_TIPS[currentAuthorIndex];
  const currentTip = currentAuthor.tips[currentTipIndex];

  useEffect(() => {
    // Rotate tips every 30 seconds
    const interval = setInterval(() => {
      setCurrentTipIndex(prev => (prev + 1) % currentAuthor.tips.length);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [currentAuthor]);

  const nextAuthor = () => {
    setCurrentAuthorIndex(prev => (prev + 1) % AUTHOR_TIPS.length);
    setCurrentTipIndex(0);
    setImageError(false);
  };

  const prevAuthor = () => {
    setCurrentAuthorIndex(prev => (prev - 1 + AUTHOR_TIPS.length) % AUTHOR_TIPS.length);
    setCurrentTipIndex(0);
    setImageError(false);
  };

  const nextTip = () => {
    setCurrentTipIndex(prev => (prev + 1) % currentAuthor.tips.length);
  };

  // Collapsed state - just a floating button
  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed right-4 top-1/2 -translate-y-1/2 z-40 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-l-lg shadow-lg hover:shadow-xl transition-all group"
        title="Writing Tips from Famous Authors"
      >
        <Lightbulb className="w-5 h-5 group-hover:scale-110 transition-transform" />
      </button>
    );
  }

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Feather className="w-5 h-5" />
            <span className="font-semibold">Writing Tips</span>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Author Image & Name - Compact */}
      <div className="relative h-20 bg-gradient-to-r from-purple-600 to-blue-600 flex items-center px-4 gap-3">
        <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white/30 bg-white/10 flex-shrink-0">
          {!imageError ? (
            <img 
              src={currentAuthor.image} 
              alt={currentAuthor.author}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white text-xl font-bold">
              {currentAuthor.author.charAt(0)}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold truncate">{currentAuthor.author}</p>
          <div className="flex gap-1">
            <button 
              onClick={prevAuthor}
              className="text-white/70 hover:text-white p-1"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button 
              onClick={nextAuthor}
              className="text-white/70 hover:text-white p-1"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Current Tip */}
      <div className="flex-1 p-4 overflow-y-auto">
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2 mb-2">
            <Lightbulb className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-gray-500">Tip {currentTipIndex + 1} of {currentAuthor.tips.length}</p>
          </div>
          <blockquote className="text-gray-700 italic leading-relaxed">
            "{currentTip}"
          </blockquote>
        </div>

        <Button 
          onClick={nextTip} 
          variant="outline" 
          className="w-full"
        >
          Next Tip <ChevronRight className="w-4 h-4 ml-1" />
        </Button>

        {/* All Tips List */}
        <div className="mt-6">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">All Tips from {currentAuthor.author}</p>
          <div className="space-y-2">
            {currentAuthor.tips.map((tip, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentTipIndex(idx)}
                className={`w-full text-left text-xs p-2 rounded transition-colors ${
                  idx === currentTipIndex 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tip.substring(0, 50)}...
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 border-t bg-gray-50 text-center">
        <p className="text-xs text-gray-500">Rotate through {AUTHOR_TIPS.length} legendary authors</p>
      </div>
    </div>
  );
}

export default AuthorTipsSidebar;
